import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import moment, { momentPropTypes } from '@g4/moment-utc';

import Layout from '@g4/boneyard/lib/Layout';
import DateRangePicker from '../DateRangePicker';
import { MODE_START, MODE_END, TYPE_RANGE } from '../Boneyard/DateRangePicker'; //'@g4/boneyard/lib/DataEntry/DateRangePicker';

import hooks, {
  NAVIGATE_MONTH,
  DISPLAY_MONTH_AND_YEAR,
} from '@g4/automation-hooks';

import {
  addOneMonthTo,
  subtractOneMonthTo,
  genMonthRestrictions,
  calcFirstCalendarScope,
  calcSingleCalendarScope,
  calcSecondCalendarScope,
} from '../../utils';

import Text from '../Boneyard/Text';

import leftIcon from '../../images/icon-45-arrow-left-blue.svg';
import rightIcon from '../../images/icon-45-arrow-right-blue.svg';
import leftIconDisabled from '../../images/icon-45-arrow-left-grey.svg';
import rightIconDisabled from '../../images/icon-45-arrow-right-grey.svg';

const FIRST_CALENDAR = 'firstCalendar';
const SECOND_CALENDAR = 'secondCalendar';

const { Row, Col } = Layout;

const DatePickerWrapper = styled.div`
  padding: 20px;
  width: 100%;
  position: absolute;
  z-index: 2;
  right: 0;
  top: 10px;
  display: flex;
  border-radius: 8px;
  min-height: 430px;
  justify-content: center;
  background: ${props => props.theme.colors.hob.datePicker.background};
  ${props => props.theme.query.md} {
    margin: -5px 10px 0 0;
    width: auto;
  }
`;

export const MonthName = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;

  ${props => props.theme.query.lg} {
    margin-bottom: ${props => props.theme.spacing(4)};
    justify-content: flex-start;
  }
`;

const MonthSwitcher = styled.div`
  color: #01579b;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 40px;
`;

const SwitchButton = styled.button.attrs({ type: 'button' })`
  align-self: center;
  ${props =>
    props.disabled &&
    css`
      cursor: default;
    `}
`;

const ArrowImage = styled.img`
  width: 45px;
`;

const MonthNameFragment = props => (
  <React.Fragment>
    <MonthName>
      <Text automationHook={DISPLAY_MONTH_AND_YEAR} size={20}>
        {props.scope.format('MMMM YYYY')}
      </Text>
    </MonthName>
  </React.Fragment>
);

MonthNameFragment.propTypes = {
  scope: momentPropTypes.utc,
};

export default class DatePicker extends React.Component {
  static propTypes = {
    onChangeCallback: PropTypes.func,
    singleCalendar: PropTypes.bool,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    value: PropTypes.any,
    mode: PropTypes.oneOf([MODE_START, MODE_END]),
    start: PropTypes.any,
    end: PropTypes.any,
  };

  static defaultProps = {
    onChange: Function.prototype,
    disabled: false,
    singleCalendar: false,
    onChangeCallback: Function.prototype,
  };

  constructor(props) {
    super(props);

    const { mode, start, end, singleCalendar } = props;

    this.state = {
      [FIRST_CALENDAR]: singleCalendar
        ? calcSingleCalendarScope(mode, start, end)
        : calcFirstCalendarScope(mode, start, end),
      [SECOND_CALENDAR]: calcSecondCalendarScope(mode, start, end),
    };

    this.handleDateChange = this.handleDateChange.bind(this);
    this.handleAddScopeMonths = this.handleAddScopeMonths.bind(this);
    this.handlePreviousMonthsClick = this.handleAddScopeMonths.bind(this, -1);
    this.handleNextMonthsClick = this.handleAddScopeMonths.bind(this, 1);
    this.handleScopeChange = this.handleScopeChange.bind(this);
    this.handleFirstMonthScopeChange = this.handleScopeChange.bind(
      this,
      FIRST_CALENDAR
    );
    this.handleSecondMonthScopeChange = this.handleScopeChange.bind(
      this,
      SECOND_CALENDAR
    );
  }

  handleDateChange(value) {
    let date;

    if (this.props.mode === MODE_START) {
      date = value.start;
    } else {
      date = value.end;
    }

    this.props.onChange(date);
  }

  handleAddScopeMonths(amount) {
    this.setState({
      [FIRST_CALENDAR]: this.state[FIRST_CALENDAR].clone().add(
        amount,
        'months'
      ),
      [SECOND_CALENDAR]: this.state[SECOND_CALENDAR].clone().add(
        amount,
        'months'
      ),
    });
  }

  handleScopeChange(scope, month) {
    this.setState({
      [month]: scope,
    });
  }

  renderPrevButton() {
    const { start, mode, singleCalendar } = this.props;
    const restrictions = genMonthRestrictions(start, mode);
    const startLimit = restrictions.start[0];
    const prevMonthEndDay = subtractOneMonthTo(
      this.state[FIRST_CALENDAR]
    ).endOf('month');

    const enabled =
      prevMonthEndDay.isAfter(moment(startLimit)) ||
      prevMonthEndDay.isSame(moment(startLimit));

    return (
      <SwitchButton
        disabled={!enabled}
        {...hooks.hook(NAVIGATE_MONTH, 'previous')}
        onClick={this.handlePreviousMonthsClick}
        singleCalendar={singleCalendar}
        isPrevious={true}
      >
        <ArrowImage
          src={enabled ? leftIcon : leftIconDisabled}
          alt="left arrow"
        />
      </SwitchButton>
    );
  }

  renderNextButton() {
    const { start, mode, singleCalendar } = this.props;
    const restrictions = genMonthRestrictions(start, mode);
    const endLimit = restrictions.end[restrictions.end.length - 1];
    const nextMonthStartDay = addOneMonthTo(
      this.state[singleCalendar ? FIRST_CALENDAR : SECOND_CALENDAR]
    );

    const enabled =
      nextMonthStartDay.isBefore(moment(endLimit)) ||
      nextMonthStartDay.isSame(moment(endLimit));

    return (
      <SwitchButton
        disabled={!enabled}
        {...hooks.hook(NAVIGATE_MONTH, 'next')}
        onClick={this.handleNextMonthsClick}
        singleCalendar={singleCalendar}
        isNext={true}
      >
        <ArrowImage
          src={enabled ? rightIcon : rightIconDisabled}
          alt="right arrow"
        />
      </SwitchButton>
    );
  }

  renderMonthSwitcher(scope) {
    return (
      <MonthSwitcher>
        {this.renderPrevButton()}
        <MonthNameFragment scope={scope} />
        {this.renderNextButton()}
      </MonthSwitcher>
    );
  }

  render() {
    const { start, end, mode, singleCalendar } = this.props;
    const restrictions = genMonthRestrictions(start, mode);

    return (
      <DatePickerWrapper>
        <React.Fragment>
          {!singleCalendar && this.renderPrevButton()}
          <Row>
            <Col sm={12}>
              <DateRangePicker
                scope={this.state[FIRST_CALENDAR]}
                onScopeChange={this.handleFirstMonthScopeChange}
                dateRangePickerDepartingButton={'Check-in'}
                dateRangePickerReturningButton={'Check-out'}
                previousMonthAriaLabel={'previous month'}
                leftArrowAltAttribute={'left arrow'}
                nextMonthAriaLabel={'next month'}
                rightArrowAltAttribute={'right arrow'}
                dateRangePickerNoAvailableDates={'No available dates'}
                dateRangePickerSelectedSameDay={'Same day selected'}
                dateRangePickerSelectDate={'Select a date'}
                checkIconAltAttribute={'check icon'}
                type={TYPE_RANGE}
                start={start}
                end={end}
                dateRangePickerSelectedDay={Function.prototype}
                onChange={this.handleDateChange}
                restrictions={restrictions}
                exclusive={true}
                mode={mode}
                renderTabs={false}
                monthSwitcher={
                  singleCalendar ? (
                    this.renderMonthSwitcher(this.state[FIRST_CALENDAR])
                  ) : (
                    <MonthNameFragment scope={this.state[FIRST_CALENDAR]} />
                  )
                }
                renderSelectionMessage={false}
                highlightBothSelections
              />
            </Col>
            {!singleCalendar && (
              <React.Fragment>
                <Col sm={12}>
                  <DateRangePicker
                    scope={this.state[SECOND_CALENDAR]}
                    onScopeChange={this.handleSecondMonthScopeChange}
                    dateRangePickerDepartingButton={'Check-in'}
                    dateRangePickerReturningButton={'Check-out'}
                    previousMonthAriaLabel={'previous month'}
                    leftArrowAltAttribute={'left arrow'}
                    nextMonthAriaLabel={'next month'}
                    rightArrowAltAttribute={'right arrow'}
                    dateRangePickerNoAvailableDates={'No available dates'}
                    dateRangePickerSelectedSameDay={'Same day selected'}
                    dateRangePickerSelectDate={'Select a date'}
                    checkIconAltAttribute={'check icon'}
                    type={TYPE_RANGE}
                    start={start}
                    end={end}
                    dateRangePickerSelectedDay={Function.prototype}
                    onChange={this.handleDateChange}
                    restrictions={restrictions}
                    exclusive={false}
                    mode={mode}
                    renderTabs={false}
                    monthSwitcher={
                      singleCalendar ? (
                        this.renderMonthSwitcher(this.state[SECOND_CALENDAR])
                      ) : (
                        <MonthNameFragment
                          scope={this.state[SECOND_CALENDAR]}
                        />
                      )
                    }
                    renderSelectionMessage={false}
                    highlightBothSelections
                  />
                </Col>
              </React.Fragment>
            )}
          </Row>
          {!singleCalendar && this.renderNextButton()}
        </React.Fragment>
      </DatePickerWrapper>
    );
  }
}
